import { useState } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { useLeagueLogo } from "../../../data/LeagueEnvironmentProvider";
import { PlayerStatsRow } from "../../../models/PlayerStats";

/**
 * Crest, headshot, name, and team/position beneath — the whole identity of a row
 * in one cell.
 *
 * Team used to be its own column, but split across two columns the eye has to
 * reassemble "who is this" from two places on every row. Together they read as a
 * single unit and free the width for stats.
 */

/** The repo's established headshot source (see `ReportCard`, `CrashGoal`). */
export const HEADSHOT_URL = "https://cdn.nba.com/headshots/nba/latest/1040x760";

/**
 * The cut-out treatment from the Team Report player header
 * (`TeamReport/PlayerPage/Header/PlayerHeader.tsx`), dialled back for a table row.
 *
 * That page is a 200px hero where a hard black keyline reads as deliberate; at
 * 32px, repeated down 560 rows, the same keyline is most of the image and
 * competes with the names it's meant to support. Two adjustments:
 *
 * - Offsets are 1px, not the hero's 2px, which would eat the face at this size.
 * - The white ring stays all the way round — that's what separates the figure
 *   from the row — but the hard black ring becomes one soft grey shadow. A hard
 *   dark edge doesn't scale down: its weight stays constant while the image
 *   shrinks, so at row size it dominates. (Tried a half-ring of two black offsets
 *   in between; still too heavy.)
 *
 * The brightness push is gentler than the hero's 125%, which blows out small.
 */
const OUTLINE_OFFSETS = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
];
export const CUTOUT_FILTER = [
  "grayscale(100%)",
  "brightness(108%)",
  "contrast(105%)",
  ...OUTLINE_OFFSETS.map(([x, y]) => `drop-shadow(${x}px ${y}px 0 white)`),
  "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.32))",
].join(" ");

/** Hides itself if the image 404s rather than leaving a broken-image glyph.
 *  Plenty of players have no crest or headshot — every prospect, for one. */
export function OptionalImage({
  src,
  alt,
  width,
  height,
  style,
}: {
  src: string | null;
  alt: string;
  width: number;
  height: number;
  style?: React.CSSProperties;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <Box
      sx={{
        width,
        height,
        flex: `0 0 ${width}px`,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {src && !failed && (
        <img
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          width={width}
          height={height}
          style={{ objectFit: "contain", ...style }}
        />
      )}
    </Box>
  );
}

export default function PlayerIdentityCell({
  row,
  to,
  showHeadshot,
}: {
  row: PlayerStatsRow;
  to: string;
  /** Only the NBA garden has headshots — `blitzId` is an nbaId there, and a
   *  Sportradar id for prospects, which this CDN knows nothing about. */
  showHeadshot: boolean;
}) {
  const getLogoUrl = useLeagueLogo();
  // The crest follows the team he played the most games for; the label still
  // lists every team, so a traded player reads as "LAC, CLE" under one crest.
  const primary = row.teams?.[0];
  const logo = primary?.nbaTeamId ? getLogoUrl(Number(primary.nbaTeamId)) : null;
  const headshot =
    showHeadshot && row.blitzId ? `${HEADSHOT_URL}/${row.blitzId}.png` : null;

  return (
    <Stack direction="row" alignItems="center" spacing={1} width="100%">
      <OptionalImage src={logo} alt={primary?.label ?? ""} width={32} height={32} />
      <OptionalImage
        src={headshot}
        alt={row.name}
        width={28}
        height={32}
        // `cover` from the top: the source is a wide crop with the player centred,
        // so filling a portrait box keeps the face and drops the empty sides.
        style={{
          objectFit: "cover",
          objectPosition: "top",
          filter: CUTOUT_FILTER,
        }}
      />
      <Stack minWidth={0} justifyContent="center">
        <MuiLink
          component={RouterLink}
          to={to}
          underline="always"
          sx={{
            fontWeight: 700,
            fontSize: 14.5,
            lineHeight: 1.2,
            color: "#17191c",
            textDecorationColor: "#c9ced4",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&:hover": { color: "primary.main", textDecorationColor: "inherit" },
          }}
        >
          {row.name}
        </MuiLink>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            color: "#8a8a8a",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {[row.team, row.pos].filter(Boolean).join(" · ") || "—"}
        </Typography>
      </Stack>
    </Stack>
  );
}
